import { mapActions, mapGetters } from 'vuex';
import { opportunityService } from '@/services/opportunity.service';
import slackService from '@/services/slack.service';
import { tenantHasChromeExtensionAccess, tenantHasTasksAccess } from '@/util/module-access-util';

// How long to keep announcing ourselves, and how quickly a ping lost to the
// content script's injection is followed by another.
const PING_WINDOW_MS = 9000;
const PING_INTERVAL_MS = 500;

export default {
  data() {
    return { pingIntervalId: null };
  },
  mounted() {
    if (!tenantHasChromeExtensionAccess()) return;

    window.addEventListener('aloha:pong', this.onHealthCheck);
    window.addEventListener('aloha:isCredentialsEntered', this.updateCredentialsEntered);
    window.addEventListener('aloha:getTasks', this.updateTasks);
    window.addEventListener('aloha:tabClosed', this.onTabClosed);
    window.addEventListener('aloha:documentsDownloaded', this.onDocumentsDownloaded);
    window.addEventListener('aloha:taskStuck', this.onTaskStuck);
    window.addEventListener('aloha:taskError', this.onTaskError);

    this.startPingingExtension();
  },
  beforeDestroy() {
    window.removeEventListener('aloha:pong', this.onHealthCheck);
    window.removeEventListener('aloha:isCredentialsEntered', this.updateCredentialsEntered);
    window.removeEventListener('aloha:getTasks', this.updateTasks);
    window.removeEventListener('aloha:tabClosed', this.onTabClosed);
    window.removeEventListener('aloha:documentsDownloaded', this.onDocumentsDownloaded);
    window.removeEventListener('aloha:taskStuck', this.onTaskStuck);
    window.removeEventListener('aloha:taskError', this.onTaskError);
    this.stopPingingExtension();
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
  },
  methods: {
    ...mapActions('fieldsExtraction', ['startExtraction']),
    ...mapActions('chromeExtension', [
      'setHealthCheck',
      'setTasks',
      'setIsCredentialsEntered',
      'refreshOpportunity',
      'setDocumentsDownloaded',
    ]),
    // The extension answers a ping by dispatching `aloha:pong`, so a missing
    // extension just means this never runs. That is the common case, not a
    // failure: nothing waits on it and nothing is reported.
    onHealthCheck() {
      this.stopPingingExtension();
      this.setHealthCheck(true);
      this.getIsCredentialsEntered();
      this.getTasks();
    },
    // A postMessage sent before the extension's content script has registered
    // its listener is lost, so a single ping at mount can race the injection
    // and go unanswered forever. Re-announce for a few seconds instead;
    // whichever ping lands first gets the pong. We handshake again even when an
    // earlier mount already found the extension: the task/credential fetches hang
    // off the pong, and listeners are dropped in beforeDestroy, so skipping it on a
    // remount would leave whatever happened in between unseen.
    startPingingExtension() {
      const deadline = Date.now() + PING_WINDOW_MS;
      const ping = () => {
        if (Date.now() >= deadline) {
          this.stopPingingExtension();
          return;
        }
        window.postMessage({ action: 'ping' }, '*');
      };
      ping();
      this.pingIntervalId = setInterval(ping, PING_INTERVAL_MS);
    },
    stopPingingExtension() {
      if (this.pingIntervalId) {
        clearInterval(this.pingIntervalId);
        this.pingIntervalId = null;
      }
    },
    updateCredentialsEntered(payload) {
      this.setIsCredentialsEntered(payload.detail.isCredentialsEntered);
    },
    getIsCredentialsEntered() {
      window.postMessage({ action: 'isCredentialsEntered' }, '*');
    },
    getTasks() {
      window.postMessage({ action: 'getTasks' }, '*');
    },
    updateTasks(payload) {
      this.setTasks(payload.detail.tasks.tasks);
    },
    async onTabClosed(payload) {
      this.getTasks();

      const oppId = payload.detail.message.task.opportunityId;
      const taskType = payload.detail.message.task.type;
      if (!oppId) {
        console.warn('No opportunity ID found in the payload');
        return;
      }
      const refreshedOpp = await this.refreshOpportunity(oppId);
      if (taskType === 'documentsRetrieval') {
        this.triggerLlmExtractions(refreshedOpp);
      }
    },
    async onDocumentsDownloaded(payload) {
      const { opportunityId, downloadedDocuments, failedDocuments } = payload.detail;
      this.setDocumentsDownloaded({ opportunityId, downloadedDocuments, failedDocuments });
      const opportunity = await this.refreshOpportunity(opportunityId);
      if (failedDocuments && failedDocuments.length > 0 && this.$refs.documentsDownloadedDialog) {
        this.$refs.documentsDownloadedDialog.openDialog({
          opportunityName: opportunity.name,
          downloadedDocuments,
          failedDocuments,
        });
      }
    },
    onTaskStuck(payload) {
      const { stepName, version } = payload.detail;
      if (this.$refs.chromeExtensionStuckDialog) {
        this.$refs.chromeExtensionStuckDialog.openDialog({ stepName });
      }
      const email = this.currentUser ? this.currentUser.email : 'unknown';
      const text = `The chrome extension got stuck:
      extension version: ${version}
      user: ${email}
      date: ${new Date().toUTCString()}
      step: '${stepName}'
      `;
      slackService.sendText({ text }).catch((error) => {
        console.warn('Failed to notify Slack of stuck task', error);
      });
    },
    onTaskError(payload) {
      const { errorCode, errorMessage, context, version } = payload.detail;
      if (errorCode === 'MISSING_REQUIRED_ROLE' && this.$refs.missingRequiredSeaoRoleDialog) {
        this.$refs.missingRequiredSeaoRoleDialog.openDialog();
      }
      const email = this.currentUser ? this.currentUser.email : 'unknown';
      const text = `The chrome extension had the following error:
      extension version: ${version}
      user: ${email}
      date: ${new Date().toUTCString()}
      error code: ${errorCode}
      error message: ${errorMessage}
      error context: ${JSON.stringify(context)}
      `;
      slackService.sendText({ text }).catch((error) => {
        console.warn('Failed to notify Slack of stuck task', error);
      });
    },
    triggerLlmExtractions(opportunity) {
      // If new documents have been downloaded by the chrome extension, then need to trigger extractions
      if (opportunity.documents.length > 0) {
        this.startExtraction({
          opportunityId: opportunity.id,
          extractionType: tenantHasTasksAccess() ? 'all_extractions' : 'fields_extraction',
        });
      }
    },
  },
};
